import {
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    alpha,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import {
    socialMediaService,
    PLATFORM_INFO,
    DashboardAnalytics,
} from "../../../api/services/socialMediaService";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ThumbUpRoundedIcon from "@mui/icons-material/ThumbUpRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

const premiumCard = {
    borderRadius: "16px",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.02)",
    overflow: "hidden",
};

const sectionIconBox = (color: string) => ({
    width: 36,
    height: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(color, 0.08),
    border: `1px solid ${alpha(color, 0.12)}`,
    color: color,
});

const StatsCard = ({
    icon,
    label,
    value,
    color,
}: {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    color: string;
}) => (
    <Card sx={premiumCard}>
        <Box
            sx={{
                position: "relative",
                p: 3,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.4)})`,
                },
            }}
        >
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#94A3B8",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            mb: 1,
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "32px",
                            fontWeight: 800,
                            color: "#0F172A",
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                        }}
                    >
                        {typeof value === "number"
                            ? new Intl.NumberFormat().format(value)
                            : value}
                    </Typography>
                </Box>
                <Box sx={sectionIconBox(color)}>{icon}</Box>
            </Box>
        </Box>
    </Card>
);

const SocialAnalytics = () => {
    const queryClient = useQueryClient();
    const [days, setDays] = useState(30);

    const { data, isLoading } = useQuery(
        ["social-analytics", days],
        () => socialMediaService.getAnalytics(days)
    );

    const pullMutation = useMutation(
        () => socialMediaService.pullAnalytics(),
        {
            onSuccess: () => {
                toast.success("Analytics sync started");
                queryClient.invalidateQueries("social-analytics");
            },
            onError: () => {
                toast.error("Failed to sync analytics");
            },
        }
    );

    if (isLoading) return <Loader />;

    const analytics: DashboardAnalytics | undefined = data?.data;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A" }}>
                        Social Analytics
                    </Typography>
                    <Typography sx={{ color: "#64748B", mt: 0.5 }}>
                        Cross-platform performance insights
                    </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Period</InputLabel>
                        <Select
                            value={days}
                            onChange={(e) => setDays(e.target.value as number)}
                            label="Period"
                        >
                            <MenuItem value={7}>7 days</MenuItem>
                            <MenuItem value={14}>14 days</MenuItem>
                            <MenuItem value={30}>30 days</MenuItem>
                            <MenuItem value={60}>60 days</MenuItem>
                            <MenuItem value={90}>90 days</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshRoundedIcon />}
                        onClick={() => pullMutation.mutate()}
                        disabled={pullMutation.isLoading}
                    >
                        Sync Now
                    </Button>
                </Box>
            </Box>

            {/* Totals */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatsCard
                        icon={<VisibilityRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Impressions"
                        value={analytics?.totals?.totalImpressions || 0}
                        color="#7C3AED"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatsCard
                        icon={<TrendingUpRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Engagements"
                        value={analytics?.totals?.totalEngagements || 0}
                        color="#2563EB"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatsCard
                        icon={<TouchAppRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Link Clicks"
                        value={analytics?.totals?.totalClicks || 0}
                        color="#EA580C"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatsCard
                        icon={<ThumbUpRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Likes"
                        value={analytics?.totals?.totalLikes || 0}
                        color="#E4405F"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatsCard
                        icon={<ShareRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Shares"
                        value={analytics?.totals?.totalShares || 0}
                        color="#00897B"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Platform Breakdown */}
                <Grid item xs={12}>
                    <Card sx={premiumCard}>
                        <Box
                            sx={{
                                px: 2.5,
                                py: 2,
                                borderBottom: "1px solid rgba(226,232,240,0.5)",
                            }}
                        >
                            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                Platform Breakdown
                            </Typography>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Platform</TableCell>
                                    <TableCell align="right">Posts</TableCell>
                                    <TableCell align="right">Impressions</TableCell>
                                    <TableCell align="right">Engagements</TableCell>
                                    <TableCell align="right">Clicks</TableCell>
                                    <TableCell align="right">Likes</TableCell>
                                    <TableCell align="right">Shares</TableCell>
                                    <TableCell align="right">Eng. Rate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analytics?.byPlatform &&
                                    Object.entries(analytics.byPlatform).map(
                                        ([platform, data]: [string, any]) => {
                                            const info = PLATFORM_INFO[platform] || {
                                                name: platform,
                                                color: "#666",
                                                icon: "?",
                                            };
                                            const engRate =
                                                data.impressions > 0
                                                    ? (
                                                        (data.engagements / data.impressions) *
                                                        100
                                                    ).toFixed(1)
                                                    : "0";
                                            return (
                                                <TableRow key={platform}>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Box
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    borderRadius: "8px",
                                                                    backgroundColor: alpha(info.color, 0.08),
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: "14px",
                                                                }}
                                                            >
                                                                {info.icon}
                                                            </Box>
                                                            <Typography
                                                                sx={{ fontWeight: 600, fontSize: "13px" }}
                                                            >
                                                                {info.name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right">{data.posts}</TableCell>
                                                    <TableCell align="right">
                                                        {new Intl.NumberFormat().format(data.impressions)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {new Intl.NumberFormat().format(data.engagements)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {new Intl.NumberFormat().format(data.clicks)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {new Intl.NumberFormat().format(data.likes)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {new Intl.NumberFormat().format(data.shares)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={Math.min(parseFloat(engRate), 100)}
                                                                sx={{
                                                                    width: 60,
                                                                    height: 6,
                                                                    borderRadius: 3,
                                                                    backgroundColor: alpha(info.color, 0.1),
                                                                    "& .MuiLinearProgress-bar": {
                                                                        backgroundColor: info.color,
                                                                        borderRadius: 3,
                                                                    },
                                                                }}
                                                            />
                                                            <Typography
                                                                sx={{ fontSize: "12px", fontWeight: 600 }}
                                                            >
                                                                {engRate}%
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }
                                    )}
                                {(!analytics?.byPlatform ||
                                    Object.keys(analytics.byPlatform).length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                                                <Typography sx={{ color: "#94A3B8" }}>
                                                    No analytics data yet. Publish some posts first!
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                            </TableBody>
                        </Table>
                    </Card>
                </Grid>

                {/* Daily Trend */}
                {analytics?.dailyTrend && analytics.dailyTrend.length > 0 && (
                    <Grid item xs={12}>
                        <Card sx={premiumCard}>
                            <Box
                                sx={{
                                    px: 2.5,
                                    py: 2,
                                    borderBottom: "1px solid rgba(226,232,240,0.5)",
                                }}
                            >
                                <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                    Daily Activity ({days} days)
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2.5, display: "flex", gap: 0.5, alignItems: "flex-end", height: 200, overflow: "auto" }}>
                                {analytics.dailyTrend.map((day: any) => {
                                    const maxImpressions = Math.max(
                                        ...analytics.dailyTrend.map((d: any) => d.impressions || 1)
                                    );
                                    const height = Math.max(
                                        ((day.impressions / maxImpressions) * 150),
                                        2
                                    );
                                    return (
                                        <Box
                                            key={day.date}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                flex: 1,
                                                minWidth: 24,
                                            }}
                                        >
                                            <Typography
                                                sx={{ fontSize: "9px", color: "#94A3B8", mb: 0.5 }}
                                            >
                                                {day.impressions > 0
                                                    ? new Intl.NumberFormat("en", {
                                                        notation: "compact",
                                                    }).format(day.impressions)
                                                    : ""}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    width: "80%",
                                                    height: height,
                                                    borderRadius: "4px 4px 0 0",
                                                    background: `linear-gradient(180deg, #00897B, ${alpha(
                                                        "#00897B",
                                                        0.4
                                                    )})`,
                                                    transition: "height 0.3s ease",
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: "9px",
                                                    color: "#94A3B8",
                                                    mt: 0.5,
                                                    transform: "rotate(-45deg)",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {new Date(day.date).toLocaleDateString("en", {
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Card>
                    </Grid>
                )}

                {/* Top Posts */}
                {analytics?.topPosts && analytics.topPosts.length > 0 && (
                    <Grid item xs={12}>
                        <Card sx={premiumCard}>
                            <Box
                                sx={{
                                    px: 2.5,
                                    py: 2,
                                    borderBottom: "1px solid rgba(226,232,240,0.5)",
                                }}
                            >
                                <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                    Top Performing Posts
                                </Typography>
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Platform</TableCell>
                                        <TableCell>Content</TableCell>
                                        <TableCell align="right">Impressions</TableCell>
                                        <TableCell align="right">Engagements</TableCell>
                                        <TableCell align="right">Likes</TableCell>
                                        <TableCell>Posted</TableCell>
                                        <TableCell>Link</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analytics.topPosts.map((post: any) => {
                                        const info = PLATFORM_INFO[post.platform] || {
                                            name: post.platform,
                                            color: "#666",
                                            icon: "?",
                                        };
                                        return (
                                            <TableRow key={post.id}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <span>{info.icon}</span>
                                                        <Typography sx={{ fontSize: "13px" }}>
                                                            {info.name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            maxWidth: 300,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {post.content}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {new Intl.NumberFormat().format(post.impressions)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {new Intl.NumberFormat().format(post.engagements)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {new Intl.NumberFormat().format(post.likes)}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(post.postedAt).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                    {post.platformUrl && (
                                                        <IconButton
                                                            size="small"
                                                            href={post.platformUrl}
                                                            target="_blank"
                                                            component="a"
                                                        >
                                                            <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default SocialAnalytics;
